/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { EMPTY_GRANT_MEANS_NO_ACCESS } from '../constants/moduleAccessRollout'

/**
 * The current admin's module access, read from the `/v1/admin/me` payload already in the store.
 *
 * No extra request and no role branching: the server resolves `accessible_modules` through
 * `effective_module_keys`, so a Super Admin / Super Viewer arrives with every key listed. The
 * frontend therefore has one code path for everyone.
 *
 * @returns {{
 *   isReady: boolean,
 *   isUnrestricted: boolean,
 *   moduleSet: Set<string>,
 *   hasModule: (key: string|null|undefined) => boolean
 * }}
 */
export const useModuleAccess = () => {
  const admin = useSelector((state) => state.admin?.admin)
  const error = useSelector((state) => state.admin?.error)

  const accessibleModules = admin?.accessible_modules

  return useMemo(() => {
    // `/me` never arrived. There is nothing to gate on, the menu is only a convenience, and the
    // server stays the real authority once enforcement is on — so allow rather than strand the user
    // on a blank app because one request failed.
    const requestFailed = !admin && Boolean(error)

    // The field missing altogether — an older server, a stale cached payload — is not the same
    // answer as an empty list, and must not lock a working user out of their whole app. An empty
    // array *is* a real answer and is honoured, but only once EMPTY_GRANT_MEANS_NO_ACCESS says the
    // accessible_module_keys backfill has actually run — see moduleAccessRollout.js.
    const fieldAbsent = Boolean(admin) && !Array.isArray(accessibleModules)
    const emptyBeforeBackfill =
      Boolean(admin) &&
      !EMPTY_GRANT_MEANS_NO_ACCESS &&
      Array.isArray(accessibleModules) &&
      accessibleModules.length === 0

    const isUnrestricted = requestFailed || fieldAbsent || emptyBeforeBackfill
    const moduleSet = new Set(Array.isArray(accessibleModules) ? accessibleModules : [])

    return {
      isReady: Boolean(admin) || requestFailed,
      isUnrestricted,
      moduleSet,
      // A null/undefined key means the caller declared no module, which is not a denial.
      hasModule: (key) => !key || isUnrestricted || moduleSet.has(key),
    }
  }, [admin, accessibleModules, error])
}

export default useModuleAccess
